import React from "react";

import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";

import { Inline, Pressable, Text, xcss } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";

interface TabPillProps {
    icon: React.ReactNode;
    text: MessageDescriptor;
    onSelect: () => void;
    isSelected?: boolean;
}

const tabPillCommonStyle = xcss({
    "borderRadius": "90px",
    "padding": "unset",
    "border": `1px solid ${token("color.background.neutral")}`,
    ":focus": {
        outlineColor: "color.border.focused",
        outlineWidth: "border.width.focused",
        outlineStyle: "solid"
    }
});

const tabPillSelectedStyle = xcss({
    "borderWidth": "border.width",
    "borderStyle": "solid",
    "borderColor": "color.border.selected",
    "color": "color.text.brand",
    "backgroundColor": "color.background.selected",
    ":hover": {
        backgroundColor: "color.background.selected.hovered"
    }
});

const tabPillDefaultStyle = xcss({
    "color": "color.text",
    "backgroundColor": "color.background.neutral",
    ":hover": {
        backgroundColor: "color.background.neutral.hovered"
    }
});

const tabPillContentStyle = xcss({
    height: "32px",
    paddingBlock: "space.075",
    paddingInline: "space.150"
});

export const TabPill: React.FC<TabPillProps> = ({
    icon,
    text,
    isSelected = false,
    onSelect
}) => {
    const intl = useIntl();

    return (
        <Pressable
            xcss={[
                tabPillCommonStyle,
                isSelected ? tabPillSelectedStyle : tabPillDefaultStyle
            ]}
            onClick={onSelect}
            role="tab"
            aria-selected={isSelected}
            aria-controls={intl.formatMessage(text)}
        >
            <Inline
                alignBlock="center"
                xcss={tabPillContentStyle}
                space="space.050"
            >
                {icon}
                <Text color={isSelected ? "color.text.brand" : "color.text"}>
                    <FormattedMessage {...text} />
                </Text>
            </Inline>
        </Pressable>
    );
};
